import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, MessageSquare, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_9c3ced01-7873-4460-b2ba-221ae55b6a5f/artifacts/palgt8p6_OpenSov%20Logo%20.png";

export default function SharedChatPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/chat/shared/${shareId}`);
        if (!res.ok) { setError('Chat not found or link expired.'); return; }
        setChat(await res.json());
      } catch { setError('Failed to load shared chat.'); }
      finally { setLoading(false); }
    })();
  }, [shareId]);

  if (loading) return (
    <div className="min-h-screen bg-app flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-app flex flex-col items-center justify-center text-content gap-4 px-4">
      <p className="text-gray-400">{error}</p>
      <button onClick={() => navigate('/chat')} className="text-emerald-500 text-sm hover:underline">Go to OpenSov AI</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-app text-content" data-testid="shared-chat-page">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-line bg-app/90 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <img src={LOGO_DARK} alt="OpenSov" className="w-7 h-7 object-contain rounded-full" />
          <div>
            <h1 className="text-sm font-medium text-content truncate max-w-[250px] md:max-w-none">{chat.title}</h1>
            <p className="text-[10px] text-gray-500">Shared conversation</p>
          </div>
        </div>
        <button onClick={() => navigate('/chat')}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-emerald-500 text-btn-foreground font-medium hover:bg-emerald-500/100 transition-all active:scale-95"
          data-testid="try-opensov-btn">
          Try OpenSov AI
        </button>
      </header>

      {/* Messages */}
      <div className="max-w-3xl mx-auto px-3 md:px-4 py-6 space-y-5">
        {chat.messages?.map((msg, i) => (
          <div key={i} className={`flex gap-2 md:gap-3 items-start ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            {msg.role === 'user' ? (
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-overlay-strong">
                <span className="text-[10px] md:text-xs font-bold text-gray-300">U</span>
              </div>
            ) : (
              <img src={LOGO_DARK} alt="" className="w-7 h-7 md:w-8 md:h-8 object-contain rounded-full flex-shrink-0" />
            )}
            <div className={`max-w-[88%] md:max-w-[85%] min-w-0 ${msg.role === 'user' ? 'text-right' : ''}`}>
              {msg.role === 'user' ? (
                <div className="inline-block rounded-2xl rounded-tr-sm px-3 md:px-4 py-2 md:py-2.5 text-[13px] md:text-sm bg-overlay-strong text-gray-100 leading-relaxed">
                  {msg.content}
                </div>
              ) : (
                <div className="prose prose-sm prose-invert max-w-none text-gray-200 leading-relaxed text-[13px] md:text-sm">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}
                    components={{
                      code({ inline, children, ...props }) {
                        return inline ? (
                          <code className="bg-overlay-strong text-emerald-500 px-1 py-0.5 rounded text-[11px] md:text-xs font-mono" {...props}>{children}</code>
                        ) : (
                          <pre className="bg-surface border border-line rounded-lg p-3 md:p-4 overflow-x-auto my-2 text-[11px] md:text-xs">
                            <code className="font-mono text-gray-300" {...props}>{children}</code>
                          </pre>
                        );
                      },
                      p({ children }) { return <p className="mb-2.5 last:mb-0">{children}</p>; },
                      strong({ children }) { return <strong className="text-content font-semibold">{children}</strong>; },
                    }}>
                    {msg.content}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer CTA */}
      <div className="sticky bottom-0 border-t border-line bg-app/90 backdrop-blur-sm px-4 py-4 text-center">
        <p className="text-xs text-gray-500 mb-2">Want to chat with OpenSov AI?</p>
        <button onClick={() => navigate('/chat')}
          className="px-6 py-2.5 rounded-xl bg-emerald-500 text-btn-foreground font-semibold text-sm hover:bg-emerald-500/100 transition-all active:scale-[0.98]"
          data-testid="start-chatting-btn">
          Start Chatting
        </button>
      </div>
    </div>
  );
}
